import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { ReadinessChecklistItem } from '@/components/organizations/ReadinessChecklistItem'
import { usePathHelper } from '@/hooks/usePathHelper'
import { ConnectAccountStatus } from '@/lib/enums/ConnectAccountStatus'
import {
  organizationQueryKeys,
  useMarkOrganizationLiveMutation,
  useMarkOrganizationLostMutation,
  useOrganizationLifecycleReadiness,
} from '@/lib/services/organizations/organizations.query'
import { stripeHubLink, stripeOnboardingLink } from '@/lib/stripe-financial-routes'
import { getErrorMessage } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { CircleCheckBig, CircleX, Info, Loader2, Send, Store } from 'lucide-react'
import { toast } from 'sonner'

const lifecycleCheckDefinitions = [
  {
    source: 'requirements',
    key: 'stripe',
    title: 'Stripe payments',
    description: "Customers can't check out",
  },
  {
    source: 'advisories',
    key: 'default_location',
    title: 'Default location',
    description: 'Recommended for storefront fulfillment',
    advisory: true,
  },
  {
    source: 'requirements',
    key: 'verified_domain',
    title: 'Verified domain',
    description: "Customers can't reach the website",
  },
  {
    source: 'advisories',
    key: 'notification_recipients',
    title: 'Enabled notifications',
    description: 'Recommended for new order alerts',
    advisory: true,
  },
  {
    source: 'requirements',
    key: 'active_location',
    title: 'Active location',
    description: 'At least one location must be active',
  },
  {
    source: 'requirements',
    key: 'storefront_product',
    title: 'Storefront product',
    description: 'There is nothing to sell online',
  },
  {
    source: 'advisories',
    key: 'collections',
    title: 'Product collections',
    description: 'Recommended to help customers browse',
    advisory: true,
  },
]

function MarkLostConfirmationSummary() {
  return (
    <div className="rounded-md border border-status-destructive-border bg-status-destructive-background p-3">
      <Typography variant="label">Marking this website as Lost will:</Typography>
      <div className="mt-2 grid gap-2 text-sm text-muted-foreground">
        <span className="flex items-start gap-2">
          <CircleX className="mt-0.5 size-4 shrink-0 text-destructive" />
          Set website status to Lost
        </span>
        <span className="flex items-start gap-2">
          <Store className="mt-0.5 size-4 shrink-0 text-interaction" />
          Show Coming Soon on the storefront
        </span>
        <span className="flex items-start gap-2">
          <Send className="mt-0.5 size-4 shrink-0 text-interaction" />
          Send a Slack notification
        </span>
        <span className="flex items-start gap-2">
          <CircleX className="mt-0.5 size-4 shrink-0 text-destructive" />
          Stop campaign emails while Lost
        </span>
        <span className="flex items-start gap-2">
          <CircleCheckBig className="mt-0.5 size-4 shrink-0 text-success" />
          Keep the account and access enabled
        </span>
      </div>
    </div>
  )
}

export function FloristLifecycleDialog({ organization, open, onOpenChange }) {
  const isMarkLost = organization.florist_status === 'live'
  const queryClient = useQueryClient()
  const path = usePathHelper()
  const readinessQuery = useOrganizationLifecycleReadiness(organization.id, {
    enabled: open && !isMarkLost,
  })
  const markLiveMutation = useMarkOrganizationLiveMutation()
  const markLostMutation = useMarkOrganizationLostMutation()
  const mutation = isMarkLost ? markLostMutation : markLiveMutation

  const targetPath = (pathHelper) => pathHelper.index().using({ orgId: organization.id }).link
  const stripeStatus = readinessQuery.data?.requirements?.stripe?.status
  const stripeLink = [
    ConnectAccountStatus.NotCreated.Value,
    ConnectAccountStatus.Incomplete.Value,
  ].includes(stripeStatus)
    ? stripeOnboardingLink(organization.id)
    : stripeHubLink(organization.id)
  const requirementLinks = {
    stripe: stripeLink,
    verified_domain: targetPath(path.domains),
    active_location: targetPath(path.locations),
    storefront_product: targetPath(path.products),
  }
  const advisoryLinks = {
    default_location: targetPath(path.locations),
    notification_recipients: targetPath(path.notifications),
    collections: targetPath(path.collections),
  }

  const handleOpenChange = (nextOpen) => {
    onOpenChange(nextOpen)
  }

  const handleConfirm = async () => {
    try {
      await mutation.mutateAsync(organization.id)
      toast.success(
        isMarkLost
          ? 'Organization marked Lost successfully'
          : 'Organization marked Live successfully'
      )
      onOpenChange(false)
    } catch (error) {
      const responseChecklist = error?.response?.data

      if (!isMarkLost && responseChecklist?.requirements) {
        queryClient.setQueryData(
          organizationQueryKeys.lifecycleReadiness(organization.id),
          responseChecklist
        )
      }

      toast.error(getErrorMessage(error))
    }
  }

  const checklist = readinessQuery.data
  const canConfirmLive = checklist?.is_ready === true

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent size={isMarkLost ? 'small' : 'default'}>
        <AlertDialogHeader>
          <AlertDialogTitle className={isMarkLost ? 'inline-flex items-center gap-2' : undefined}>
            {isMarkLost ? <Info className="size-4" /> : null}
            {isMarkLost ? 'Mark Lost?' : 'Mark Live'}
          </AlertDialogTitle>
          <AlertDialogDescription
            className={
              isMarkLost ? 'sr-only' : checklist && !canConfirmLive ? 'text-destructive' : undefined
            }
          >
            {isMarkLost
              ? `Mark ${organization.name} as Lost? Their website will show Coming Soon and campaign emails will stop until they are marked Live again. This sends a Slack notification but keeps their account and access enabled.`
              : checklist && !canConfirmLive
                ? 'Review the checklist and complete any items shown in red.'
                : 'Review the website checklist before marking it Live.'}
          </AlertDialogDescription>
          {isMarkLost ? <MarkLostConfirmationSummary /> : null}
        </AlertDialogHeader>

        {!isMarkLost ? (
          <ScrollArea
            className="-mr-2 max-h-[60vh] pr-3"
            viewportClassName="max-h-[60vh] overscroll-y-contain"
          >
            <div>
              {readinessQuery.isFetching && !checklist ? (
                <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                  <Loader2 className="size-5 animate-spin" />
                  Checking website readiness...
                </div>
              ) : readinessQuery.isError && !checklist ? (
                <div className="rounded-md border border-destructive/40 p-4 text-sm text-destructive">
                  Unable to load website readiness. Close the dialog and try again.
                </div>
              ) : (
                <div className="grid gap-3">
                  {lifecycleCheckDefinitions.map((definition) => (
                    <ReadinessChecklistItem
                      key={definition.key}
                      description={definition.description}
                      href={
                        definition.advisory
                          ? advisoryLinks[definition.key]
                          : requirementLinks[definition.key]
                      }
                      incompleteVariant={definition.advisory ? 'warning' : 'destructive'}
                      isReady={checklist?.[definition.source]?.[definition.key]?.is_ready === true}
                      mutedDescription
                      title={definition.title}
                    />
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button
            type="button"
            variant={isMarkLost ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={mutation.isPending || (!isMarkLost && !canConfirmLive)}
          >
            {mutation.isPending ? 'Saving...' : isMarkLost ? 'Mark Lost' : 'Mark Live'}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
